import {
  wishSchema,
  type SaveWineRatingRequest,
  type Suggestions,
  type WineDetail,
} from "@corklog/shared";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router";

import {
  createWine,
  fetchSuggestions,
  fetchWine,
  meetWish,
  saveWineRating,
  updateWine,
} from "../api.js";
import { BackToWines } from "../components/BackToWines.js";
import { emptyWine, WineForm, type WineFormValues } from "../components/WineForm.js";
import { wishFormValues } from "../wish.js";

/**
 * Registering a wine, and correcting one. One screen, because they are one
 * act: the fields that describe a wine do not depend on whether it is new.
 *
 * A page rather than a form folded into the list, and rather than a section
 * that unfolds on the wine's own page. Ten fields is not a thing you do in
 * passing — it is done sitting down with a bottle in front of you, and it
 * should have the screen to itself while it is being done. It also keeps the
 * list what it is for: finding a bottle, not a form with an inventory
 * underneath it.
 *
 * Saving leaves for the wine's own page, which is where its bottles are
 * registered. A wine with no bottles is the unusual case, so the next thing to
 * do is almost always waiting there.
 */
type State =
  | { status: "loading" }
  | { status: "ready"; initial: WineFormValues; wine: WineDetail | null }
  | { status: "failed" };

/** The wine as the form wants it. The number is not among the fields: it is
 *  printed on labels on bottles standing in a cabinet. */
function formValues(wine: WineDetail): WineFormValues {
  return {
    name: wine.name,
    type: wine.type,
    countryCode: wine.countryCode,
    producer: wine.producer,
    region: wine.region,
    style: wine.style,
    vintage: wine.vintage,
    grapes: wine.grapes,
    isNonVintage: wine.isNonVintage,
    baseVintage: wine.baseVintage,
    disgorgedOn: wine.disgorgedOn,
    dosageGramsPerLitre: wine.dosageGramsPerLitre,
    alcoholPercent: wine.alcoholPercent,
    drinkFrom: wine.drinkFrom,
    peakFrom: wine.peakFrom,
    peakTo: wine.peakTo,
    drinkBy: wine.drinkBy,
  };
}

export function WineEditor() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cellarId = "", wineId } = useParams();
  const correcting = wineId !== undefined;

  // A wish being bought arrives as router state from the wish list (10.64), and
  // opens the registration on its facts. Parsed rather than trusted: history
  // state survives a reload and can be anything the address bar left behind,
  // and a shape that does not parse is simply no wish — the empty form.
  const location = useLocation() as { state: unknown };
  const wish = correcting
    ? null
    : (() => {
        const parsed = wishSchema.safeParse(
          (location.state as { wish?: unknown } | null)?.wish,
        );
        return parsed.success && parsed.data.metAt === null ? parsed.data : null;
      })();

  const [state, setState] = useState<State>(
    correcting
      ? { status: "loading" }
      : {
          status: "ready",
          initial: wish === null ? emptyWine : wishFormValues(wish),
          wine: null,
        },
  );
  const [suggestions, setSuggestions] = useState<Suggestions | null>(null);

  useEffect(() => {
    void fetchSuggestions(cellarId)
      .then(setSuggestions)
      .catch(() => setSuggestions(null));
  }, [cellarId]);

  useEffect(() => {
    if (wineId === undefined) return;
    void fetchWine(cellarId, wineId)
      .then((wine) => setState({ status: "ready", initial: formValues(wine), wine }))
      .catch(() => setState({ status: "failed" }));
  }, [cellarId, wineId]);

  const list = `/kallare/${cellarId}`;
  const back = correcting ? `${list}/viner/${wineId}` : list;

  const leave = useCallback(
    (to: string) => {
      // Replace rather than push: the form is finished with, and Back from the
      // wine should reach the list rather than the form that made it.
      void navigate(to, { replace: true });
    },
    [navigate],
  );

  /**
   * Save, and then the readings that came with it.
   *
   * The ratings are written after the wine because they need its id, and they
   * are written one at a time because each may create a rating source — two
   * requests naming `Munskänkarna` at the same instant would race for the same
   * reference row. Sequential is also the order they were typed in, which is
   * the order a failure is easiest to make sense of.
   *
   * A rating that fails does not fail the registration: the wine is already
   * catalogued and has a number, and the page it lands on has the ratings
   * section open. Losing the wine to save the reading would be the wrong way
   * round.
   */
  const submit = async (values: WineFormValues, ratings: SaveWineRatingRequest[]) => {
    if (correcting) {
      await updateWine(cellarId, wineId, values);
      leave(back);
      return;
    }
    const wine = await createWine(cellarId, values);
    // The wish is met once the wine exists, and not before: a registration
    // that fails leaves the wish open. And a wish that cannot be marked does
    // not fail the registration — the wine is catalogued and has its number,
    // and the wish can be removed by hand. The same order as the ratings.
    if (wish !== null) {
      await meetWish(cellarId, wish.id, wine.id).catch(() => undefined);
    }
    for (const rating of ratings) {
      await saveWineRating(cellarId, wine.id, rating).catch(() => undefined);
    }
    leave(`${list}/viner/${wine.id}`);
  };

  return (
    <section>
      <BackToWines cellarId={cellarId} />

      <div className="flex items-baseline justify-between gap-4">
        <h1 className="font-display text-3xl">
          {correcting ? t("wine.editing") : t("wines.add")}
        </h1>
        {/* The wine a correction belongs to, once its name is known. The list
            is one step up and already above the heading; this is the step the
            form is standing on. */}
        {state.status === "ready" && state.wine !== null && (
          <Link
            to={back}
            className="text-accent text-sm underline-offset-4 hover:underline"
          >
            {state.wine.name}
          </Link>
        )}
      </div>

      {state.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("wine.loadFailed")}
        </p>
      )}

      {state.status === "ready" && (
        <div className="mt-6">
          <WineForm
            initial={state.initial}
            suggestions={suggestions}
            submitLabel={correcting ? t("wine.saveEdit") : t("wines.add")}
            busyLabel={t("wines.saving")}
            onCancel={() => leave(back)}
            onSubmit={submit}
            offerRatings={!correcting}
            catalogue={
              // Only when registering. A wine being corrected is in the
              // catalogue and would match itself, and *register a lot on the
              // existing number instead* is not an answer to editing — the
              // entry is already there.
              correcting ? [] : (suggestions?.wines ?? [])
            }
            cellarId={cellarId}
          />
        </div>
      )}
    </section>
  );
}
